import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Captions, CaptionsOff, Volume2, VolumeX } from 'lucide-react';
import { getSpanishCaptions } from '@/utils/captionMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Detect a YouTube URL → returns the video ID, or null.
function getYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.trim());
    if (u.hostname === 'youtu.be') return u.pathname.replace('/', '').split('?')[0] || null;
    if (u.hostname.endsWith('youtube.com') || u.hostname.endsWith('youtube-nocookie.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0] === 'shorts' && parts[1]) return parts[1];
      if (parts[0] === 'embed' && parts[1]) return parts[1];
    }
  } catch (e) { /* not a valid URL */ }
  return null;
}

/**
 * Video Player Component with Spanish Caption Support + YouTube fallback
 * 
 * Features:
 * - Direct files (.mp4/.webm/.mov): native <video> with mute/captions/volume controls + Spanish captions via proxy
 * - YouTube URLs: privacy-friendly youtube-nocookie iframe (no captions/mute/onEnded support — YouTube provides its own player)
 */
function VideoPlayer({ 
  src, 
  onEnded,
  onTimeUpdate,
  className = "",
  poster = null,
  videoRef: externalRef = null
}) {
  const { t, i18n } = useTranslation();
  const internalRef = useRef(null);
  const videoRef = externalRef || internalRef;
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [showCaptionNotice, setShowCaptionNotice] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showUnmuteHint, setShowUnmuteHint] = useState(false);

  const ytId = useMemo(() => getYouTubeId(src), [src]);

  // Toggle captions on the video element (no-op when YouTube — videoRef won't be a <video>)
  useEffect(() => {
    if (ytId) return;
    const video = videoRef.current;
    if (!video || !video.textTracks || video.textTracks.length === 0) return;
    const track = video.textTracks[0];
    if (track) {
      track.mode = captionsEnabled ? 'showing' : 'hidden';
    }
  }, [captionsEnabled, videoRef, ytId]);

  // ===== YOUTUBE BRANCH =====
  // For YouTube URLs we render a privacy-friendly iframe.
  // Caption controls / onEnded / mute UI don't apply (YouTube has its own player).
  if (ytId) {
    const ytSrc = `https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`;
    return (
      <div
        className={`relative w-full overflow-hidden rounded-lg shadow-lg bg-black ${className}`}
        style={{ paddingTop: '56.25%' }}  // 16:9
        data-testid="lesson-video-youtube"
      >
        <iframe
          src={ytSrc}
          title="Lesson video"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  const isSpanish = i18n.language === 'es';
  const { hasSpanishCaptions, captionsSrc } = getSpanishCaptions(src);

  // Generate proxied video URL for CORS support
  const getProxiedVideoUrl = (videoUrl) => {
    if (!videoUrl) return '';
    // Use proxy for external videos to enable CORS for captions
    if (videoUrl.includes('customer-assets.emergentagent.com') || 
        videoUrl.includes('emergentagent.com')) {
      return `${BACKEND_URL}/api/video-proxy?url=${encodeURIComponent(videoUrl)}`;
    }
    return videoUrl;
  };

  const proxiedSrc = getProxiedVideoUrl(src);

  // Captions are opt-in - user must click the button to enable
  // No auto-enable, even for Spanish users

  // Handle when video starts playing
  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    // Ensure volume is set
    if (video.volume === 0) {
      video.volume = 1.0;
    }
    
    // If browser muted the video due to autoplay policy, show hint
    if (video.muted) {
      setShowUnmuteHint(true);
      setIsMuted(true);
    }
  };

  // Handle click anywhere on video container - unmute
  const handleContainerClick = (e) => {
    // Don't unmute if clicking on controls or buttons
    if (e.target.closest('button') || e.target.tagName === 'VIDEO') {
      return;
    }
    
    const video = videoRef.current;
    if (video && video.muted) {
      video.muted = false;
      video.volume = 1.0;
      setIsMuted(false);
      setShowUnmuteHint(false);
    }
  };

  // Toggle mute/unmute
  const handleToggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    
    const newMuted = !video.muted;
    video.muted = newMuted;
    if (!newMuted && video.volume === 0) {
      video.volume = 1.0;
    }
    setIsMuted(newMuted);
    setShowUnmuteHint(false);
  };

  // Track volume changes from native controls
  const handleVolumeChange = (e) => {
    const video = e.target;
    setIsMuted(video.muted || video.volume === 0);
  };

  // Set volume when video loads
  const handleLoadedData = (e) => {
    e.target.volume = 1.0;
    // Don't force unmute - let browser policy decide
    setIsMuted(e.target.muted);
  };

  const handleToggleCaptions = () => {
    if (!hasSpanishCaptions) {
      setShowCaptionNotice(true);
      setTimeout(() => setShowCaptionNotice(false), 3000);
      return;
    }
    setCaptionsEnabled(!captionsEnabled);
  };

  const defaultPoster = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450'%3E%3Crect width='800' height='450' fill='%23fef3c7'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-size='48' font-family='Arial'%3E▶ Play Video%3C/text%3E%3C/svg%3E";

  return (
    <div className="relative" onClick={handleContainerClick}>
      <video
        key={proxiedSrc}
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        className={`w-full rounded-lg shadow-lg ${className}`}
        onEnded={onEnded}
        onTimeUpdate={onTimeUpdate}
        onPlay={handlePlay}
        onVolumeChange={handleVolumeChange}
        onLoadedData={handleLoadedData}
        poster={poster || defaultPoster}
        data-testid="video-player"
      >
        <source src={proxiedSrc} type="video/mp4" />
        {/* Spanish Captions Track */}
        {hasSpanishCaptions && captionsSrc && (
          <track
            kind="captions"
            src={captionsSrc}
            srcLang="es"
            label="Español"
            default={isSpanish}
          />
        )}
        {t('video.notSupported', 'Your browser does not support the video tag.')}
      </video>
      
      {/* Unmute hint overlay - shows when browser blocks audio */}
      {showUnmuteHint && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg cursor-pointer pointer-events-none"
          data-testid="unmute-hint"
        >
          <div 
            className="bg-white/95 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-pulse pointer-events-auto cursor-pointer"
            onClick={handleToggleMute}
          >
            <VolumeX className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="font-bold text-gray-800">{t('video.clickToUnmute', 'Click to enable sound')}</p>
              <p className="text-sm text-gray-600">{t('video.tapForAudio', 'Tap anywhere for audio')}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Mute/Unmute button - always visible for quick access */}
      <button
        onClick={handleToggleMute}
        className={`absolute bottom-16 left-3 z-10 p-2 rounded-lg transition-all shadow-md ${
          isMuted
            ? 'bg-red-500 text-white hover:bg-red-600'
            : 'bg-black/60 text-white hover:bg-black/80'
        }`}
        title={isMuted ? t('video.unmute', 'Unmute') : t('video.mute', 'Mute')}
        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        data-testid="mute-toggle-btn"
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5" />
        ) : (
          <Volume2 className="w-5 h-5" />
        )}
      </button>

      {/* Caption Toggle Button - Only show when captions available */}
      {hasSpanishCaptions && (
        <button
          onClick={handleToggleCaptions}
          className={`absolute bottom-16 left-14 z-10 p-2 rounded-lg transition-all shadow-md ${
            captionsEnabled
              ? 'bg-yellow-500 text-white hover:bg-yellow-600'
              : 'bg-black/60 text-white hover:bg-black/80'
          }`}
          title={captionsEnabled ? t('video.captionsOn', 'Captions ON') : t('video.captionsOff', 'Captions OFF')}
          aria-label={captionsEnabled ? 'Turn captions off' : 'Turn captions on'}
          data-testid="captions-toggle-btn"
        >
          {captionsEnabled ? (
            <Captions className="w-5 h-5" />
          ) : (
            <CaptionsOff className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Spanish Caption Indicator */}
      {isSpanish && hasSpanishCaptions && captionsEnabled && (
        <div className="absolute top-3 left-3 bg-yellow-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
          CC ES
        </div>
      )}

      {/* Caption Status Notice for non-captioned videos */}
      {showCaptionNotice && !hasSpanishCaptions && (
        <div 
          className="absolute bottom-20 left-3 bg-gray-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-fade-in"
          data-testid="captions-notice"
        >
          {t('video.captionsComingSoon', 'Subtítulos en español próximamente')}
        </div>
      )}

      {/* Caption availability indicator for Spanish users */}
      {isSpanish && !hasSpanishCaptions && (
        <div 
          className="mt-2 text-sm text-gray-500 bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2"
          data-testid="no-spanish-captions-notice"
        >
          <CaptionsOff className="w-4 h-4" />
          <span>{t('video.captionsComingSoon', 'Subtítulos en español próximamente')}</span>
        </div>
      )}
    </div>
  );
}

export default VideoPlayer;
